// Shared Joins editor for the Schema (M) and Business Area (A) designers.
// Three ways to define the joins between a dataset's tables:
//   • Diagram    — a draggable node graph (fact starred, solid = direct join,
//                  dashed = path join); click a line to edit that join's
//                  conditions with the SAME editor as the List tab; add tables.
//   • List       — per-join cards: type, table→table, one-or-more column pairs
//                  (composite keys) OR a free-text raw ON expression.
//   • Instruction — (Business Area only) describe ALL joins in plain language;
//                  when made authoritative the Assistant writes the SQL from it
//                  (for joins too conditional to declare). See JOIN_MODE.
//
// JOIN_CONDITION stays the single source of truth for a join's ON clause: the
// column-pair editor serializes to `LT.a = RT.a AND LT.b = RT.b`; free-text
// edits it verbatim (trusted, inlined by the SQL builder).
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Boxes, Plus, Trash2, X } from 'lucide-react'
import type { JoinLayout, SchemaJoin, SchemaTable, SchemaTableColumn } from '../visual/types'
import {
  JOIN_TERM_OPS,
  NULLARY_JOIN_OPS,
  blankJoinGroup,
  blankJoinTerm,
  parseColumnPairs,
  parseJoinCondition,
  serializeJoinCondition,
  tableColumns,
  type JoinConditionGroup as JoinConditionGroupType,
  type JoinConditionModel,
  type JoinTerm,
} from '../visual/joinHelpers'

const JOIN_TYPE_OPTIONS = ['INNER', 'LEFT', 'RIGHT', 'FULL'] as const
const MONO = "var(--font-mono, 'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace)"

export type JoinsView = 'diagram' | 'list' | 'instruction'

export interface JoinsEditorProps {
  tables: SchemaTable[]
  mainTable?: string
  joins: SchemaJoin[]
  canEdit: boolean
  onJoinsChange: (next: SchemaJoin[]) => void
  layout?: JoinLayout
  onLayoutChange?: (next: JoinLayout) => void
  /** Open the host designer's add/import-table flow from the diagram. */
  onAddTable?: () => void
  /** Lets the key pickers offer a table's REAL columns, not just the ones the
   *  model happens to list. Without it a model written with a partial column
   *  list (an agent curating a wide master down to a handful) leaves the join
   *  key unselectable — the dropdown cannot offer what the model omitted, and
   *  the existing condition renders blank because its value is not an option.
   *  `load` returns the full physical column list for one table; `adopt` adds
   *  a chosen source column to the model, so picking it as a key also puts it
   *  in the dataset (the SQL builder requires the key to be listed there). */
  columnSource?: {
    load: (table: string) => Promise<SchemaTableColumn[]>
    adopt: (table: string, column: SchemaTableColumn) => void
  }
  /** Business-Area-only Instruction tab. Omit → only Diagram | List. */
  instruction?: {
    value: string
    onChange: (v: string) => void
    authoritative: boolean
    onAuthoritativeChange: (v: boolean) => void
  }
}

// ── small style helpers ──────────────────────────────────────────────
const selectStyle: React.CSSProperties = {
  padding: '6px 8px', fontSize: 14.5, borderRadius: 7,
  border: '1px solid var(--color-border)', background: 'var(--color-input-bg, var(--color-bg))',
  color: 'var(--color-text)',
}
const cardStyle: React.CSSProperties = {
  border: '1px solid var(--color-border)', borderRadius: 12,
  background: 'var(--color-bg-secondary)', padding: 14, marginBottom: 14,
}

function nextJoinId(joins: SchemaJoin[]): string {
  let max = 0
  for (const j of joins) {
    const m = /^J(\d+)$/.exec(j.JOIN_ID || '')
    if (m) max = Math.max(max, parseInt(m[1], 10))
  }
  return `J${String(max + 1).padStart(3, '0')}`
}

function plainTerms(j: SchemaJoin): string {
  if (!j.LEFT_TABLE || !j.RIGHT_TABLE) return 'pick both tables to define the join'
  if (!(j.JOIN_CONDITION || '').trim()) return 'no ON condition yet — add a condition, or type it'
  const pairs = parseColumnPairs(j.JOIN_CONDITION, j.LEFT_TABLE, j.RIGHT_TABLE)
  if (pairs.length) {
    const rightNoun = j.RIGHT_TABLE.toLowerCase().replace(/^dim_/, '').replace(/_/g, ' ')
    return `each ${j.LEFT_TABLE} row is matched to its ${rightNoun}`
  }
  const model = parseJoinCondition(j.JOIN_CONDITION)
  if (model) {
    const n = model.groups.length
    return `conditional join — ${n} condition set${n === 1 ? '' : 's'}`
  }
  return 'custom expression — written by hand'
}

// ── source columns: the real table, not just what the model lists ────
// Supplied by JoinsEditor from its `columnSource` prop and read directly by
// the term rows (a context, so the diagram's click-through editor and the
// list cards share ONE lazily-filled cache instead of re-introspecting per
// row). Absent (`null`) → the pickers behave exactly as before.
interface SourceColumnsApi {
  /** Real columns of `table` that the model does NOT list yet. */
  extra: (table: string) => string[]
  /** Ask for a table's columns (lazy, cached, one request per table). */
  need: (table: string) => void
  loading: (table: string) => boolean
  /** Pull a source column into the model — a join key MUST be listed there. */
  adopt: (table: string, dbname: string) => void
}
const SourceColumnsCtx = createContext<SourceColumnsApi | null>(null)

function useSourceColumns(
  tables: SchemaTable[], columnSource: JoinsEditorProps['columnSource'],
): SourceColumnsApi | null {
  const [cache, setCache] = useState<Record<string, SchemaTableColumn[]>>({})
  const [busy, setBusy] = useState<Record<string, boolean>>({})
  // Requests in flight / already answered — a ref so `need` stays stable and
  // an effect calling it on every render cannot loop.
  const asked = useRef<Set<string>>(new Set())
  const load = columnSource?.load
  const adoptCol = columnSource?.adopt

  const need = useCallback((table: string) => {
    const t = (table || '').trim().toUpperCase()
    if (!t || !load || asked.current.has(t)) return
    asked.current.add(t)
    setBusy((b) => ({ ...b, [t]: true }))
    load(t)
      .then((cols) => setCache((c) => ({ ...c, [t]: cols })))
      // A source that cannot be read (connection down, table dropped) must not
      // break the editor — the picker just falls back to the model's columns.
      .catch(() => setCache((c) => ({ ...c, [t]: [] })))
      .finally(() => setBusy((b) => ({ ...b, [t]: false })))
  }, [load])

  const modelCols = useMemo(() => {
    const m: Record<string, Set<string>> = {}
    for (const t of tables) {
      const name = (t.TABLE_NAME || '').toUpperCase()
      if (!name) continue
      m[name] = new Set((t.COLUMNS || []).map((c) => (c.DBNAME || '').toUpperCase()))
    }
    return m
  }, [tables])

  return useMemo(() => {
    if (!load || !adoptCol) return null
    return {
      need,
      loading: (table) => !!busy[(table || '').toUpperCase()],
      extra: (table) => {
        const t = (table || '').toUpperCase()
        const have = modelCols[t] || new Set<string>()
        return (cache[t] || [])
          .map((c) => (c.DBNAME || '').toUpperCase())
          .filter((c) => c && !have.has(c))
      },
      adopt: (table, dbname) => {
        const t = (table || '').toUpperCase()
        const col = (cache[t] || []).find(
          (c) => (c.DBNAME || '').toUpperCase() === (dbname || '').toUpperCase())
        if (col) adoptCol(t, col)
      },
    }
  }, [load, adoptCol, need, busy, cache, modelCols])
}

// ── the ON builder: one term row (left · op · right / value) ─────────
function JoinTermRow({
  term, canEdit, glueLabel, leftTable, rightTable, leftCols, rightCols,
  onChange, onRemove,
}: {
  term: JoinTerm
  canEdit: boolean
  /** Combinator chip shown before rows 2+ ("OR" / "AND"). */
  glueLabel?: string
  leftTable: string
  rightTable: string
  leftCols: string[]
  rightCols: string[]
  onChange: (patch: Partial<JoinTerm>) => void
  onRemove?: () => void
}) {
  const op = (term.op || '=').toUpperCase()
  const nullary = NULLARY_JOIN_OPS.has(op)
  const src = useContext(SourceColumnsCtx)

  useEffect(() => {
    src?.need(leftTable)
    src?.need(rightTable)
  }, [src, leftTable, rightTable])

  // Every reference the pickers can offer for one table: the model's columns,
  // then the source columns it does not list yet (picking one adds it).
  const colGroup = (table: string, cols: string[]) => {
    if (!table) return null
    const extra = src?.extra(table) || []
    return (
      <>
        <optgroup label={src?.loading(table) ? `${table} (reading source…)` : table}>
          {cols.map((c) => (
            <option key={`${table}.${c}`} value={`${table}.${c}`}>{table}.{c}</option>
          ))}
        </optgroup>
        {extra.length > 0 && (
          <optgroup label={`${table} — more columns from the source (adds to dataset)`}>
            {extra.map((c) => (
              <option key={`${table}.${c}.src`} value={`${table}.${c}`}>+ {table}.{c}</option>
            ))}
          </optgroup>
        )}
      </>
    )
  }

  // A value the model can no longer offer (the column was removed from
  // TABLES[].COLUMNS after the join was written) would leave the select
  // blank — showing the join as unset and losing it on the next edit. Keep it
  // visible and flagged instead.
  const orphan = (value: string) => {
    if (!value || !value.includes('.')) return null
    const [t, c] = value.split('.')
    const known = (tbl: string, col: string) => {
      const list = tbl.toUpperCase() === (leftTable || '').toUpperCase() ? leftCols
        : tbl.toUpperCase() === (rightTable || '').toUpperCase() ? rightCols : null
      if (!list) return false
      return list.some((x) => x.toUpperCase() === col.toUpperCase())
        || (src?.extra(tbl) || []).some((x) => x.toUpperCase() === col.toUpperCase())
    }
    if (known(t, c)) return null
    return <option value={value}>{value} — not in the dataset</option>
  }

  // Picking a source column also puts it in the model: the SQL builder
  // requires a join key to be listed on its table, and the backend review
  // rejects the schema otherwise.
  const pickColumn = (value: string, apply: (v: string) => void) => {
    if (value.includes('.')) {
      const [t, c] = value.split('.')
      if ((src?.extra(t) || []).some((x) => x.toUpperCase() === c.toUpperCase())) {
        src?.adopt(t, c)
      }
    }
    apply(value)
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      {glueLabel && (
        <span style={{
          fontSize: 13, fontWeight: 700, padding: '2px 8px', borderRadius: 6,
          background: 'var(--color-surface-2)', border: '1px solid var(--color-border)',
          color: 'var(--color-text-secondary)', flexShrink: 0,
        }}>
          {glueLabel}
        </span>
      )}
      <select
        value={term.left} disabled={!canEdit}
        onChange={(e) => pickColumn(e.target.value, (v) => onChange({ left: v }))}
        style={{ ...selectStyle, flex: 1, minWidth: 130, fontFamily: MONO }}
        title="Left side of the condition — a column of either table"
      >
        <option value="">column…</option>
        {orphan(term.left)}
        {colGroup(leftTable, leftCols)}
        {colGroup(rightTable, rightCols)}
      </select>
      <select
        value={op} disabled={!canEdit}
        onChange={(e) => {
          const nextOp = e.target.value
          if (NULLARY_JOIN_OPS.has(nextOp)) onChange({ op: nextOp, right: '', rightKind: 'lit' })
          else if (nullary) onChange({ op: nextOp, right: '', rightKind: 'col' })
          else onChange({ op: nextOp })
        }}
        style={{ ...selectStyle, width: 110, flexShrink: 0 }}
        title="Operator"
      >
        {JOIN_TERM_OPS.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
      {!nullary && (
        <>
          <select
            value={term.rightKind === 'lit' ? '__lit__' : term.right}
            disabled={!canEdit}
            onChange={(e) => {
              const v = e.target.value
              if (v === '__lit__') onChange({ rightKind: 'lit', right: '' })
              else pickColumn(v, (col) => onChange({ rightKind: 'col', right: col }))
            }}
            style={{ ...selectStyle, flex: 1, minWidth: 130, fontFamily: MONO }}
            title="Right side — a column, or a fixed value"
          >
            <option value="">column…</option>
            {term.rightKind !== 'lit' && orphan(term.right)}
            {colGroup(leftTable, leftCols)}
            {colGroup(rightTable, rightCols)}
            <option value="__lit__">fixed value…</option>
          </select>
          {term.rightKind === 'lit' && (
            <input
              value={term.right} readOnly={!canEdit}
              onChange={(e) => onChange({ right: e.target.value })}
              placeholder="e.g. Y or 42"
              title="Fixed value — strings are quoted automatically"
              style={{
                width: 110, padding: '6px 8px', fontSize: 14, fontFamily: MONO,
                borderRadius: 7, border: '1px solid var(--color-border)',
                background: 'var(--color-input-bg, var(--color-bg))', color: 'var(--color-text)',
              }}
            />
          )}
        </>
      )}
      {onRemove && canEdit && (
        <button className="btn btn-ghost btn-sm" onClick={onRemove} title="Remove this condition">
          <X size={13} />
        </button>
      )}
    </div>
  )
}

// ── the ON builder: one condition SET (terms combined with its own and/or) ──
function JoinConditionGroupBox({
  group, canEdit, removable, leftTable, rightTable, leftCols, rightCols,
  onChange, onRemove,
}: {
  group: JoinConditionGroupType
  canEdit: boolean
  removable: boolean
  leftTable: string
  rightTable: string
  leftCols: string[]
  rightCols: string[]
  onChange: (next: JoinConditionGroupType) => void
  onRemove: () => void
}) {
  const glue = group.condition === 'and' ? 'AND' : 'OR'
  const patchTerm = (ti: number, patch: Partial<JoinTerm>) =>
    onChange({ ...group, terms: group.terms.map((t, i) => (i === ti ? { ...t, ...patch } : t)) })
  return (
    <div style={{
      border: '1px solid var(--color-border)', borderRadius: 10, padding: 10,
      background: 'var(--color-bg)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        <span style={{
          fontSize: 13, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
          color: 'var(--color-text-secondary)',
        }}>
          Condition set
        </span>
        {group.terms.length > 1 && (
          <>
            <select
              value={group.condition} disabled={!canEdit}
              onChange={(e) => onChange({ ...group, condition: e.target.value as 'and' | 'or' })}
              title="How the conditions INSIDE this set combine"
              style={{ ...selectStyle, width: 'auto', padding: '2px 6px', fontSize: 13 }}
            >
              <option value="or">match ANY (OR)</option>
              <option value="and">match ALL (AND)</option>
            </select>
            <span style={{ fontSize: 13, color: 'var(--color-text-faint)' }}>
              renders as ( … {glue} … )
            </span>
          </>
        )}
        {removable && canEdit && (
          <button
            className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }}
            onClick={onRemove} title="Remove this condition set"
          >
            <Trash2 size={13} />
          </button>
        )}
      </div>
      <div style={{ display: 'grid', gap: 6 }}>
        {group.terms.map((t, ti) => (
          <JoinTermRow
            key={ti}
            term={t}
            canEdit={canEdit}
            glueLabel={ti > 0 ? glue : undefined}
            leftTable={leftTable}
            rightTable={rightTable}
            leftCols={leftCols}
            rightCols={rightCols}
            onChange={(patch) => patchTerm(ti, patch)}
            onRemove={group.terms.length > 1
              ? () => onChange({ ...group, terms: group.terms.filter((_, i) => i !== ti) })
              : undefined}
          />
        ))}
      </div>
      {canEdit && (
        <button
          onClick={() => onChange({ ...group, terms: [...group.terms, blankJoinTerm()] })}
          title={`Add an alternative — conditions in this set combine with ${glue}`}
          style={{
            marginTop: 8, background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--color-accent)', fontWeight: 600, fontSize: 14, padding: 0,
          }}
        >
          + {glue} condition
        </button>
      )}
    </div>
  )
}

// ── one join card (used by both List and the Diagram's line editor) ──
function JoinRow({
  join, idx, tables, canEdit, onPatch, onRemove,
}: {
  join: SchemaJoin
  idx: number
  tables: SchemaTable[]
  canEdit: boolean
  onPatch: (patch: Partial<SchemaJoin>) => void
  onRemove: () => void
}) {
  const freeText = !!join.FREE_TEXT
  // Local condition-set state so half-filled terms stay editable. Re-seeded
  // whenever the tables / mode change (JoinRow is keyed on those in the
  // parent). An existing condition beyond the builder's grammar (functions,
  // nested parens, …) forces free-text so it is never silently rewritten.
  const [seed] = useState(() => parseJoinCondition(join.JOIN_CONDITION))
  const [cond, setCond] = useState<JoinConditionModel>(
    () => seed ?? { joiner: 'and', groups: [blankJoinGroup()] },
  )
  const unbuildable = !seed && !!(join.JOIN_CONDITION || '').trim()
  const effectiveFreeText = freeText || unbuildable

  const commit = (next: JoinConditionModel) => {
    setCond(next)
    onPatch({ JOIN_CONDITION: serializeJoinCondition(next) })
  }
  const setTable = (side: 'LEFT_TABLE' | 'RIGHT_TABLE', value: string) => {
    // Changing a table invalidates the existing condition.
    onPatch({ [side]: value, JOIN_CONDITION: '' })
  }

  const leftCols = tableColumns(tables, join.LEFT_TABLE)
  const rightCols = tableColumns(tables, join.RIGHT_TABLE)
  const generated = effectiveFreeText
    ? (join.JOIN_CONDITION || '')
    : serializeJoinCondition(cond)

  return (
    <div style={cardStyle}>
      {/* header: id · type · left → right · free-text toggle · trash */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <input
          value={join.JOIN_ID} readOnly={!canEdit}
          onChange={(e) => onPatch({ JOIN_ID: e.target.value })}
          title="Join id" placeholder="J001"
          style={{
            width: 60, textAlign: 'center', fontFamily: MONO, fontSize: 13.5, fontWeight: 700,
            padding: '5px 4px', borderRadius: 7, border: '1px solid var(--color-border)',
            background: 'var(--color-surface-2)', color: 'var(--color-text-secondary)',
          }}
        />
        <select
          value={join.JOIN_TYPE} disabled={!canEdit}
          onChange={(e) => onPatch({ JOIN_TYPE: e.target.value })}
          style={{ ...selectStyle, width: 92, fontWeight: 600 }}
        >
          {JOIN_TYPE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select
          value={join.LEFT_TABLE} disabled={!canEdit}
          onChange={(e) => setTable('LEFT_TABLE', e.target.value)}
          style={{ ...selectStyle, flex: 1, minWidth: 130, fontFamily: MONO }}
        >
          <option value="">left table…</option>
          {tables.map((t) => t.TABLE_NAME && <option key={t.TABLE_NAME} value={t.TABLE_NAME}>{t.TABLE_NAME}</option>)}
        </select>
        <span style={{ color: 'var(--color-text-secondary)', fontWeight: 700 }}>→</span>
        <select
          value={join.RIGHT_TABLE} disabled={!canEdit}
          onChange={(e) => setTable('RIGHT_TABLE', e.target.value)}
          style={{ ...selectStyle, flex: 1, minWidth: 130, fontFamily: MONO }}
        >
          <option value="">right table…</option>
          {tables.map((t) => t.TABLE_NAME && <option key={t.TABLE_NAME} value={t.TABLE_NAME}>{t.TABLE_NAME}</option>)}
        </select>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
          {canEdit && (
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => onPatch({ FREE_TEXT: !effectiveFreeText })}
              disabled={unbuildable && effectiveFreeText}
              title={unbuildable
                ? 'This expression is beyond the visual builder (functions / nested parens) — edit it as text'
                : effectiveFreeText
                  ? 'Switch back to the visual condition builder'
                  : 'Type the ON condition as a raw expression'}
              style={{
                fontSize: 13, color: effectiveFreeText ? 'var(--color-accent)' : 'var(--color-text-secondary)',
                border: effectiveFreeText ? '1px solid var(--color-accent)' : '1px solid transparent',
              }}
            >
              ✎ free text
            </button>
          )}
          {canEdit && (
            <button className="btn btn-ghost btn-sm" onClick={onRemove} title="Remove join">
              <Trash2 size={13} />
            </button>
          )}
        </div>
      </div>

      {/* body: condition-set builder OR free-text expression */}
      {effectiveFreeText ? (
        <textarea
          value={join.JOIN_CONDITION} readOnly={!canEdit}
          onChange={(e) => onPatch({ JOIN_CONDITION: e.target.value })}
          placeholder="Raw ON condition, e.g. FACT_SALES.date_id = DIM_DATE.date_id AND DIM_DATE.is_open = 'Y'"
          rows={2}
          style={{
            marginTop: 10, width: '100%', fontFamily: MONO, fontSize: 14, resize: 'vertical',
            padding: '8px 10px', borderRadius: 8, border: '1px solid var(--color-border)',
            background: 'var(--color-input-bg, var(--color-bg))', color: 'var(--color-text)',
          }}
        />
      ) : (
        <div style={{ marginTop: 10, display: 'grid', gap: 8 }}>
          {cond.groups.map((g, gi) => (
            <div key={gi}>
              {gi > 0 && (
                /* ONE outer combinator joins all the condition sets. */
                <div style={{ display: 'flex', justifyContent: 'center', margin: '2px 0 8px' }}>
                  <select
                    value={cond.joiner} disabled={!canEdit}
                    onChange={(e) => commit({ ...cond, joiner: e.target.value as 'and' | 'or' })}
                    title="How the condition sets combine with each other"
                    style={{ ...selectStyle, width: 'auto', padding: '2px 6px', fontSize: 13, fontWeight: 700 }}
                  >
                    <option value="and">AND</option>
                    <option value="or">OR</option>
                  </select>
                </div>
              )}
              <JoinConditionGroupBox
                group={g}
                canEdit={canEdit}
                removable={cond.groups.length > 1}
                leftTable={join.LEFT_TABLE}
                rightTable={join.RIGHT_TABLE}
                leftCols={leftCols}
                rightCols={rightCols}
                onChange={(next) => commit({
                  ...cond, groups: cond.groups.map((x, i) => (i === gi ? next : x)),
                })}
                onRemove={() => commit({
                  ...cond, groups: cond.groups.filter((_, i) => i !== gi),
                })}
              />
            </div>
          ))}
          {canEdit && (
            <button
              onClick={() => commit({ ...cond, groups: [...cond.groups, blankJoinGroup()] })}
              title="Add another condition set — sets combine with the AND/OR between them"
              style={{
                justifySelf: 'start', background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--color-accent)', fontWeight: 600, fontSize: 14, padding: 0,
              }}
            >
              + add condition set
            </button>
          )}
        </div>
      )}

      {/* generated condition readout + plain terms */}
      {generated && (
        <div style={{
          marginTop: 10, padding: '8px 11px', borderRadius: 8, fontFamily: MONO, fontSize: 13.5,
          background: 'var(--color-surface-2)', color: 'var(--color-text)', wordBreak: 'break-word',
        }}>
          {generated}
        </div>
      )}
      <div style={{ marginTop: 8, fontSize: 14, color: 'var(--color-text-secondary)' }}>
        In plain terms: {plainTerms(join)}
      </div>
    </div>
  )
}

// ── Diagram (draggable) ──────────────────────────────────────────────
const NODE_W = 200

function autoLayout(tables: SchemaTable[], joins: SchemaJoin[], mainTable?: string): JoinLayout {
  const names = tables.map((t) => t.TABLE_NAME).filter(Boolean)
  const adj = new Map<string, Set<string>>()
  names.forEach((n) => adj.set(n, new Set()))
  for (const j of joins) {
    if (adj.has(j.LEFT_TABLE) && adj.has(j.RIGHT_TABLE)) {
      adj.get(j.LEFT_TABLE)!.add(j.RIGHT_TABLE)
      adj.get(j.RIGHT_TABLE)!.add(j.LEFT_TABLE)
    }
  }
  const root = (mainTable && names.includes(mainTable)) ? mainTable : names[0]
  const level = new Map<string, number>()
  if (root) {
    const q = [root]
    level.set(root, 0)
    while (q.length) {
      const cur = q.shift()!
      for (const nb of adj.get(cur) || []) {
        if (!level.has(nb)) { level.set(nb, (level.get(cur) || 0) + 1); q.push(nb) }
      }
    }
  }
  let maxLvl = 0
  level.forEach((v) => { maxLvl = Math.max(maxLvl, v) })
  const byLevel = new Map<number, string[]>()
  for (const n of names) {
    const lv = level.has(n) ? level.get(n)! : maxLvl + 1
    if (!byLevel.has(lv)) byLevel.set(lv, [])
    byLevel.get(lv)!.push(n)
  }
  const out: JoinLayout = {}
  byLevel.forEach((group, lv) => {
    group.forEach((n, i) => {
      out[n] = { x: 32 + lv * 250, y: 28 + i * 108 }
    })
  })
  return out
}

function JoinsDiagram({
  tables, joins, mainTable, canEdit, layout, onLayoutChange, onAddTable, onSelectJoin, selectedJoin,
}: {
  tables: SchemaTable[]
  joins: SchemaJoin[]
  mainTable?: string
  canEdit: boolean
  layout: JoinLayout
  onLayoutChange?: (next: JoinLayout) => void
  onAddTable?: () => void
  onSelectJoin: (idx: number) => void
  selectedJoin: number | null
}) {
  const canvasRef = useRef<HTMLDivElement>(null)
  const auto = useMemo(() => autoLayout(tables, joins, mainTable), [tables, joins, mainTable])
  const posOf = useCallback((name: string) => layout[name] || auto[name] || { x: 32, y: 28 }, [layout, auto])
  const condOf = useCallback(
    (name: string) => (tables.find((t) => t.TABLE_NAME === name)?.CONDITION || '').trim(),
    [tables],
  )

  const drag = useRef<{ name: string; dx: number; dy: number } | null>(null)
  const onPointerDown = (e: React.PointerEvent, name: string) => {
    if (!canEdit || !onLayoutChange) return
    const rect = canvasRef.current?.getBoundingClientRect()
    if (!rect) return
    const p = posOf(name)
    drag.current = { name, dx: e.clientX - rect.left - p.x, dy: e.clientY - rect.top - p.y }
    ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
  }
  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current
    const rect = canvasRef.current?.getBoundingClientRect()
    if (!d || !rect || !onLayoutChange) return
    const x = Math.max(0, e.clientX - rect.left - d.dx)
    const y = Math.max(0, e.clientY - rect.top - d.dy)
    onLayoutChange({ ...layout, [d.name]: { x, y } })
  }
  const endDrag = () => { drag.current = null }

  const nodeHeight = (name: string) => (condOf(name) ? 62 : 44)
  const center = (name: string) => {
    const p = posOf(name)
    return { cx: p.x + NODE_W / 2, cy: p.y + nodeHeight(name) / 2 }
  }

  const names = tables.map((t) => t.TABLE_NAME).filter(Boolean)
  const canvasH = Math.max(
    380,
    ...names.map((n) => posOf(n).y + nodeHeight(n) + 40),
  )
  const canvasW = Math.max(
    720,
    ...names.map((n) => posOf(n).x + NODE_W + 40),
  )

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <span style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>
          Dashed lines are path joins — tables reached through another table. Drag tables to arrange; click a line to edit its join.
        </span>
        {canEdit && onAddTable && (
          <button className="btn btn-secondary btn-sm" style={{ marginLeft: 'auto' }} onClick={onAddTable}>
            <Plus size={13} /> Add table
          </button>
        )}
      </div>
      <div
        ref={canvasRef}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
        style={{
          position: 'relative', width: '100%', minWidth: canvasW, height: canvasH,
          border: '1px solid var(--color-border)', borderRadius: 14, overflow: 'hidden',
          background:
            'radial-gradient(var(--color-border) 1px, transparent 1px) 0 0 / 22px 22px, var(--color-bg)',
        }}
      >
        {/* edges */}
        <svg width={canvasW} height={canvasH} style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
          {joins.map((j, idx) => {
            if (!names.includes(j.LEFT_TABLE) || !names.includes(j.RIGHT_TABLE)) return null
            const a = center(j.LEFT_TABLE)
            const b = center(j.RIGHT_TABLE)
            const isPath = j.LEFT_TABLE !== mainTable && j.RIGHT_TABLE !== mainTable
            const sel = selectedJoin === idx
            return (
              <g key={idx}>
                <line
                  x1={a.cx} y1={a.cy} x2={b.cx} y2={b.cy}
                  stroke={sel ? 'var(--color-accent)' : 'var(--color-text-faint)'}
                  strokeWidth={sel ? 3 : 2}
                  strokeDasharray={isPath ? '6 5' : undefined}
                />
                <line
                  x1={a.cx} y1={a.cy} x2={b.cx} y2={b.cy}
                  stroke="transparent" strokeWidth={14}
                  style={{ pointerEvents: 'stroke', cursor: 'pointer' }}
                  onClick={() => onSelectJoin(idx)}
                />
              </g>
            )
          })}
        </svg>
        {/* nodes */}
        {names.map((name) => {
          const p = posOf(name)
          const cond = condOf(name)
          const isMain = name === mainTable
          return (
            <div
              key={name}
              onPointerDown={(e) => onPointerDown(e, name)}
              style={{
                position: 'absolute', left: p.x, top: p.y, width: NODE_W,
                boxSizing: 'border-box', padding: '9px 12px', borderRadius: 10,
                border: isMain ? '2px solid var(--color-warning)' : '1px solid var(--color-border)',
                background: 'var(--color-bg-secondary)', boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                cursor: canEdit && onLayoutChange ? 'grab' : 'default', userSelect: 'none',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: MONO, fontWeight: 700, fontSize: 14.5 }}>
                {isMain && <span style={{ color: 'var(--color-warning)' }}>★</span>}
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
              </div>
              {cond && (
                <div style={{ marginTop: 4, fontFamily: MONO, fontSize: 13, color: 'var(--color-warning)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={cond}>
                  ▾ {cond}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── main component ───────────────────────────────────────────────────
export default function JoinsEditor({
  tables, mainTable, joins, canEdit, onJoinsChange, layout, onLayoutChange, onAddTable, instruction,
  columnSource,
}: JoinsEditorProps) {
  const [view, setView] = useState<JoinsView>('list')
  const [selectedJoin, setSelectedJoin] = useState<number | null>(null)
  const sourceColumns = useSourceColumns(tables, columnSource)

  const patchJoin = (idx: number, patch: Partial<SchemaJoin>) =>
    onJoinsChange(joins.map((j, i) => (i === idx ? { ...j, ...patch } : j)))
  const removeJoin = (idx: number) => {
    onJoinsChange(joins.filter((_, i) => i !== idx))
    setSelectedJoin(null)
  }
  const addJoin = () => {
    onJoinsChange([...joins, {
      JOIN_ID: nextJoinId(joins), LEFT_TABLE: '', RIGHT_TABLE: '', JOIN_TYPE: 'LEFT', JOIN_CONDITION: '',
    }])
  }

  const tabBtn = (key: JoinsView, label: string) => (
    <button
      key={key}
      onClick={() => setView(key)}
      style={{
        padding: '6px 16px', fontSize: 15, fontWeight: view === key ? 700 : 500, cursor: 'pointer',
        border: 'none', borderRadius: 8,
        background: view === key ? 'var(--color-bg)' : 'transparent',
        color: view === key ? 'var(--color-text)' : 'var(--color-text-secondary)',
        boxShadow: view === key ? '0 1px 2px rgba(0,0,0,0.12)' : 'none',
      }}
    >
      {label}
    </button>
  )

  const instructionAuthoritative = !!instruction?.authoritative

  return (
    <SourceColumnsCtx.Provider value={sourceColumns}>
    <div>
      {/* header + segmented view toggle */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Joins ({joins.length})</h3>
        <div style={{ display: 'inline-flex', gap: 2, padding: 3, borderRadius: 10, background: 'var(--color-surface-2)' }}>
          {tabBtn('diagram', 'Diagram')}
          {tabBtn('list', 'List')}
          {instruction && tabBtn('instruction', 'Instruction')}
        </div>
        {instructionAuthoritative && view !== 'instruction' && (
          <span style={{ fontSize: 13.5, color: 'var(--color-warning)', fontWeight: 600 }}>
            Instruction mode is ON — these declared joins are reference only.
          </span>
        )}
      </div>

      {view === 'diagram' && (
        <>
          <JoinsDiagram
            tables={tables}
            joins={joins}
            mainTable={mainTable}
            canEdit={canEdit}
            layout={layout || {}}
            onLayoutChange={onLayoutChange}
            onAddTable={onAddTable}
            onSelectJoin={setSelectedJoin}
            selectedJoin={selectedJoin}
          />
          {selectedJoin !== null && joins[selectedJoin] && (
            <div style={{ marginTop: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span style={{ fontSize: 14.5, fontWeight: 700 }}>
                  Editing {joins[selectedJoin].JOIN_ID || `join #${selectedJoin + 1}`}
                </span>
                <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }} onClick={() => setSelectedJoin(null)}>
                  <X size={13} /> Close
                </button>
              </div>
              <JoinRow
                key={`${selectedJoin}:${joins[selectedJoin].LEFT_TABLE}:${joins[selectedJoin].RIGHT_TABLE}:${joins[selectedJoin].FREE_TEXT ? 1 : 0}`}
                join={joins[selectedJoin]}
                idx={selectedJoin}
                tables={tables}
                canEdit={canEdit}
                onPatch={(patch) => patchJoin(selectedJoin, patch)}
                onRemove={() => removeJoin(selectedJoin)}
              />
            </div>
          )}
        </>
      )}

      {view === 'list' && (
        <div style={{ maxWidth: 980 }}>
          {joins.length === 0 && (
            <div style={{ fontSize: 15, color: 'var(--color-text-secondary)', marginBottom: 12 }}>
              No joins defined. Chains are automatic: define each hop once and the query builder joins through.
            </div>
          )}
          {joins.map((j, idx) => (
            <JoinRow
              key={`${idx}:${j.LEFT_TABLE}:${j.RIGHT_TABLE}:${j.FREE_TEXT ? 1 : 0}`}
              join={j}
              idx={idx}
              tables={tables}
              canEdit={canEdit}
              onPatch={(patch) => patchJoin(idx, patch)}
              onRemove={() => removeJoin(idx)}
            />
          ))}
          {canEdit && (
            <button className="btn btn-primary btn-sm" onClick={addJoin}>
              <Plus size={13} /> Add join
            </button>
          )}
        </div>
      )}

      {view === 'instruction' && instruction && (
        <div style={{ maxWidth: 860 }}>
          <p style={{ margin: '0 0 12px', fontSize: 15, lineHeight: 1.55, color: 'var(--color-text-secondary)' }}>
            Describe how <strong>all</strong> the tables relate, in plain language — including conditional or
            computed joins that can't be declared as simple column pairs. Reference database functions by name
            (e.g. <code style={{ fontFamily: MONO }}>DATE_TRUNC</code>, <code style={{ fontFamily: MONO }}>COALESCE</code>) and the AI will use them.
          </p>
          <textarea
            value={instruction.value} readOnly={!canEdit}
            onChange={(e) => instruction.onChange(e.target.value)}
            placeholder={"e.g. FACT_SALES joins DIM_DATE on DATE_TRUNC('day', FACT_SALES.ts) = DIM_DATE.day; customers roll up to country via DIM_CUSTOMER.country_code = DIM_COUNTRY.code, but only where DIM_COUNTRY.active = 'Y'."}
            rows={8}
            style={{
              width: '100%', fontSize: 15, lineHeight: 1.6, resize: 'vertical', padding: '12px 14px',
              borderRadius: 10, border: '1px solid var(--color-border)', fontFamily: MONO,
              background: 'var(--color-input-bg, var(--color-bg))', color: 'var(--color-text)',
            }}
          />
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 14, cursor: canEdit ? 'pointer' : 'default' }}>
            <input
              type="checkbox" checked={instructionAuthoritative} disabled={!canEdit}
              onChange={(e) => instruction.onAuthoritativeChange(e.target.checked)}
              style={{ marginTop: 3 }}
            />
            <span style={{ fontSize: 14.5, lineHeight: 1.5 }}>
              <strong>Use this instruction to build queries.</strong>{' '}
              <span style={{ color: 'var(--color-text-secondary)' }}>
                The Assistant will write the SQL from this text (raw SQL) instead of the declared joins — for joins
                too conditional to declare. Note: the raw-SQL path is disabled when row/column security is active on
                this area; the query result is still checked against your row-security policies.
              </span>
            </span>
          </label>
          {instructionAuthoritative && (
            <div style={{
              marginTop: 12, padding: '10px 12px', borderRadius: 9, fontSize: 14,
              border: '1px solid var(--color-warning)', background: 'var(--color-warning-weak)', color: 'var(--color-warning)',
            }}>
              <Boxes size={13} style={{ verticalAlign: -2, marginRight: 6 }} />
              The declared joins (Diagram / List) become reference-only while this is on.
            </div>
          )}
        </div>
      )}
    </div>
    </SourceColumnsCtx.Provider>
  )
}
